import {
  CANLI_SUPHELI_ESIK_MS,
  type CanliOturumSatiri,
  yerelParcalar,
} from "@koclukpanel/contracts";
import { gunMetni } from "@koclukpanel/ui";
import { TUR_ADI } from "../../ogrenci/src/gorev-etiketleri";
import { ApiHatasi } from "./api";

/**
 * CANLI ÇALIŞMA — SAF YARDIMCILAR (F-LIV-01/03/04)
 *
 * Süre ve "son sinyal" metinleri, ayırma ve yenileme kararı burada: ekran dosyası yalnız çiziyor ve
 * bu mantık zamanlayıcı/ağ olmadan test edilebiliyor.
 */

/** Modül durumunun sorgu anahtarı — menü (Uygulama) ve sayfa AYNI önbelleği paylaşır. */
export const CANLI_MODUL_ANAHTARI = ["canli-modul"] as const;

/** Listenin sorgu anahtarı. */
export const CANLI_LISTE_ANAHTARI = ["canli-liste"] as const;

/**
 * "32 dakikadır" / "2 sa 5 dk" (F-LIV-01). Bir dakikadan kısa süre "0 dakikadır" diye değil açıkça
 * yazılıyor: sayaç yeni başlamış, ekranda sıfır görmek "hiç çalışmıyor" gibi okunurdu.
 */
export function sureYazisi(saniye: number): string {
  const toplamDk = Math.floor(Math.max(0, saniye) / 60);
  if (toplamDk < 1) return "1 dakikadan kısa süredir";
  if (toplamDk < 60) return `${toplamDk} dakikadır`;
  return `${Math.floor(toplamDk / 60)} sa ${toplamDk % 60} dk`;
}

/**
 * "3 dk önce" — sunucunun hesapladığı `an`a göre (F-LIV-03).
 *
 * Tarayıcı saatine DEĞİL sunucu anına bakılıyor: kullanıcının saati yanlışsa "son sinyal 3 saat önce"
 * gibi uydurma bir sessizlik görünürdü; sessiz/çalışıyor ayrımını zaten sunucu o ana göre yaptı.
 */
export function sonSinyalYazisi(an: string, sonSinyal: string): string {
  const fark = Math.max(0, Date.parse(an) - Date.parse(sonSinyal));
  const dk = Math.floor(fark / 60_000);
  if (dk < 1) return "az önce";
  if (dk < 60) return `${dk} dk önce`;
  return `${Math.floor(dk / 60)} sa ${dk % 60} dk önce`;
}

/**
 * "Başladı 14:05" ya da başka güne düşüyorsa "Başladı 7 Ekim 23:10" (S8-KOD-05) — Europe/Istanbul.
 *
 * Neden gün koşullu: sayaç bugün başladıysa gün yazmak mobilde satırı boşuna uzatır; ama 7 saat önce
 * başlayan şüpheli sayaç dünden kalmış olabilir ve yalnız "23:10" yazmak yanıltırdı. "Bugün" tarayıcı
 * saatine değil sunucunun `an`ına göre (`sonSinyalYazisi` ile aynı gerekçe). Çözülemeyen damgada
 * uydurma yok: boş döner, ekran hiçbir şey çizmez.
 */
export function baslangicYazisi(an: string, baslangic: string): string {
  if (Number.isNaN(Date.parse(baslangic)) || Number.isNaN(Date.parse(an))) return "";
  const b = yerelParcalar(baslangic);
  const simdi = yerelParcalar(an);
  return b.gun === simdi.gun ? `Başladı ${b.saat}` : `Başladı ${gunMetni(b.gun)} ${b.saat}`;
}

/** Konu varsa konu, yoksa görev türünün Türkçe adı (F-LIV-01); bilinmeyen tür ham haliyle geçer. */
export function satirKonusu(satir: Pick<CanliOturumSatiri, "konu" | "gorevTuru">): string {
  return satir.konu ?? TUR_ADI[satir.gorevTuru] ?? satir.gorevTuru;
}

/** "Şüpheli: 6 saatten uzun" — saat sayısı eşikten türüyor, ikinci bir "6" yazılmıyor. */
export const SUPHELI_ETIKETI = `Şüpheli: ${CANLI_SUPHELI_ESIK_MS / 3_600_000} saatten uzun`;

/** Çalışanlar ana listede, bağlantısı kopanlar ayrı bölümde (F-LIV-03); sıra sunucunun verdiği sıra. */
export function satirlariAyir(satirlar: readonly CanliOturumSatiri[]): {
  calisanlar: CanliOturumSatiri[];
  sessizler: CanliOturumSatiri[];
} {
  return {
    calisanlar: satirlar.filter((s) => s.durum === "CALISIYOR"),
    sessizler: satirlar.filter((s) => s.durum === "SESSIZ"),
  };
}

/**
 * Listenin yenileme aralığı (F-LIV-04: 30 sn, websocket yok).
 *
 * Yetki yok (403) ya da modül kapalı (404) sürekli yoklamayla düzelmez; her 30 saniyede aynı reddi
 * almak hem boşuna yük hem de sunucuda boşuna denetim kaydı. Ağ kopması gibi geçici hatada yoklama
 * SÜRER ki bağlantı gelince liste kendiliğinden dolsun.
 */
export function listeYenilemeAraligi(hata: unknown, aralikMs: number): number | false {
  if (hata instanceof ApiHatasi && (hata.durum === 403 || hata.durum === 404)) return false;
  return aralikMs;
}

/** Sunucu modülün kapalı olduğunu söylüyor mu (404 `MODUL_KAPALI`). */
export function modulKapaliHatasi(hata: unknown): boolean {
  return hata instanceof ApiHatasi && hata.durum === 404 && hata.kod === "MODUL_KAPALI";
}
